import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Search, Upload, X, Pencil, Trash2, Archive, Lock, MapPin, Eye, SlidersHorizontal, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { TeamLogo } from '@/lib/teamLogos';
import { useSeason } from '@/contexts/SeasonContext';
import api from '@/lib/api';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import MatchDetailDialog, { MATCH_LABELS } from '@/components/MatchDetailDialog';

export default function MatchesPage() {
  const { t } = useTranslation();
  const { user, hasPerm } = useAuth();
  const { seasonOpen } = useSeason();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [matches, setMatches] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showEnded, setShowEnded] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: '', home_team: '', away_team: '', round: '', match_date: '', kickoff_time: '', stadium: '', city: '', notes: '', status: 'upcoming', season: '' });

  // Dropdown data
  const [clubs, setClubs] = useState([]);
  const [stadiums, setStadiums] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [seasons, setSeasons] = useState([]);

  // Edit state
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Season filter for match list
  const [seasonFilter, setSeasonFilter] = useState('');
  const [roundFilter, setRoundFilter] = useState('');

  // Match detail view
  const [detailMatch, setDetailMatch] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Bulk delete state
  const [bulkDeleteEnabled, setBulkDeleteEnabled] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const fetchMatches = () => {
    setLoading(true);
    const params = seasonFilter && seasonFilter !== 'all' ? `?season=${seasonFilter}` : '';
    api.get(`/api/matches${params}`).then(r => setMatches(r.data)).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get('/api/clubs').then(r => setClubs(r.data)).catch(() => {});
    api.get('/api/stadiums').then(r => setStadiums(r.data)).catch(() => {});
    api.get('/api/rounds').then(r => setRounds(r.data)).catch(() => {});
    api.get('/api/seasons').then(r => {
      const all = r.data.filter(s => s.status !== 'archived');
      setSeasons(all);
      const active = all.find(s => s.status === 'active');
      if (active && !seasonFilter) setSeasonFilter(active.name);
    }).catch(() => {});
    if (hasPerm('create_matches') || hasPerm('edit_matches') || hasPerm('delete_matches')) {
      api.get('/api/settings').then(r => setBulkDeleteEnabled(!!r.data.allow_bulk_delete)).catch(() => {});
    }
  }, []);

  useEffect(() => { if (seasonFilter) fetchMatches(); }, [seasonFilter]);

  // Auto-open detail dialog from ?detail= query param (e.g. from dashboard)
  useEffect(() => {
    const detailId = searchParams.get('detail');
    if (detailId && matches.length > 0) {
      const match = matches.find(m => m.id === detailId);
      if (match) {
        setDetailMatch(match);
        setSearchParams({}, { replace: true }); // Clean the URL
      } else {
        // Match might be in ended list, fetch it directly
        api.get(`/api/matches/${detailId}`).then(r => {
          setDetailMatch(r.data);
          setSearchParams({}, { replace: true });
        }).catch(() => {});
      }
    }
  }, [matches, searchParams]);

  const handleCreate = async () => {
    if (!form.home_team || !form.away_team || !form.match_date) { toast.error(t('fill_required')); return; }
    const body = { ...form, title: `${form.home_team} vs ${form.away_team}`, season: form.season || seasonFilter };
    try { await api.post('/api/matches', body); toast.success(t('match_created')); setOpen(false); setForm({ title: '', home_team: '', away_team: '', round: '', match_date: '', kickoff_time: '', stadium: '', city: '', notes: '', status: 'upcoming' }); fetchMatches(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const toInputDate = (d) => {
    if (!d) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
    const parts = d.split('/');
    if (parts.length === 3) {
      const [a, b, c] = parts;
      if (c.length === 4) return `${c}-${a.padStart(2,'0')}-${b.padStart(2,'0')}`;
    }
    return d;
  };

  const handleEdit = (match) => {
    setEditForm({ ...match, match_date: toInputDate(match.match_date) });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm) return;
    setSaving(true);
    try {
      const { id, ...data } = editForm;
      data.title = `${data.home_team} vs ${data.away_team}`;
      await api.put(`/api/matches/${id}`, data);
      toast.success(t('match_updated') || 'Match updated');
      setEditOpen(false);
      setEditForm(null);
      fetchMatches();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!editForm) return;
    setSaving(true);
    try {
      await api.delete(`/api/matches/${editForm.id}`);
      toast.success(t('match_deleted') || 'Match deleted');
      setEditOpen(false);
      setEditForm(null);
      fetchMatches();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally { setSaving(false); setConfirmOpen(false); }
  };

  const handleSetLabel = async (matchId, label) => {
    try {
      const res = await api.put(`/api/matches/${matchId}/label`, { label });
      toast.success(label ? `Label set to ${MATCH_LABELS[label]?.name}` : 'Label removed');
      setMatches(prev => prev.map(m => m.id === matchId ? { ...m, label: res.data.label } : m));
      if (detailMatch?.id === matchId) setDetailMatch(prev => ({ ...prev, label: res.data.label }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to set label'); }
  };


  // When selecting a stadium, auto-fill city from the stadiums list
  const handleStadiumSelect = (stadiumName, setter) => {
    const found = stadiums.find(s => s.name === stadiumName);
    setter(p => ({ ...p, stadium: stadiumName, city: found ? found.city : p.city }));
  };

  const allTeams = useMemo(() => {
    const teams = new Set();
    matches.forEach(m => { if (m.home_team) teams.add(m.home_team); if (m.away_team) teams.add(m.away_team); });
    clubs.forEach(c => teams.add(c.name));
    return [...teams].sort();
  }, [matches, clubs]);

  const matchRounds = useMemo(() => {
    const roundSet = new Set();
    matches.forEach(m => { if (m.round) roundSet.add(m.round); });
    return [...roundSet].sort((a, b) => {
      const nA = parseInt(String(a).replace(/\D/g, '')) || 9999;
      const nB = parseInt(String(b).replace(/\D/g, '')) || 9999;
      return nA - nB;
    });
  }, [matches]);

  const endedCount = useMemo(() => matches.filter(m => m.status === 'ended').length, [matches]);

  const filtered = useMemo(() => {
    return matches.filter(m => {
      if (!showEnded && m.status === 'ended') return false;
      const q = search.toLowerCase();
      const matchesSearch = !q || m.home_team?.toLowerCase().includes(q) || m.away_team?.toLowerCase().includes(q) ||
        m.stadium?.toLowerCase().includes(q) || m.city?.toLowerCase().includes(q) || m.round?.toLowerCase().includes(q) ||
        m.home_team_ar?.includes(search) || m.away_team_ar?.includes(search);
      const matchesStatus = !statusFilter || statusFilter === 'all' || m.status === statusFilter;
      const matchesTeam = !teamFilter || teamFilter === 'all' || m.home_team === teamFilter || m.away_team === teamFilter;
      const matchesDate = !dateFilter || m.match_date === dateFilter;
      const matchesRound = !roundFilter || roundFilter === 'all' || m.round === roundFilter;
      return matchesSearch && matchesStatus && matchesTeam && matchesDate && matchesRound;
    });
  }, [matches, search, statusFilter, teamFilter, dateFilter, roundFilter, showEnded]);

  const groupedByRound = useMemo(() => {
    const sorted = [...filtered].sort((a, b) => {
      const rA = parseInt((a.round || '').replace(/\D/g, '')) || 9999;
      const rB = parseInt((b.round || '').replace(/\D/g, '')) || 9999;
      if (rA !== rB) return rA - rB;
      return (a.match_date || '').localeCompare(b.match_date || '');
    });
    const groups = [];
    let currentRound = null;
    for (const m of sorted) {
      const round = m.round || 'No Round';
      if (round !== currentRound) {
        groups.push({ type: 'header', round });
        currentRound = round;
      }
      groups.push({ type: 'match', data: m });
    }
    return groups;
  }, [filtered]);

  const hasFilters = search || (statusFilter && statusFilter !== 'all') || (teamFilter && teamFilter !== 'all') || dateFilter || (roundFilter && roundFilter !== 'all');
  const clearFilters = () => { setSearch(''); setStatusFilter(''); setTeamFilter(''); setDateFilter(''); setRoundFilter(''); };

  // Bulk delete
  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => {
    const ids = filtered.map(m => m.id);
    setSelected(prev => prev.size === ids.length ? new Set() : new Set(ids));
  };
  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const r = await api.post('/api/matches/bulk-delete', { ids: [...selected] });
      const { deleted, skipped } = r.data;
      if (deleted > 0) toast.success(`Deleted ${deleted} matches`);
      if (skipped?.length > 0) {
        toast.warning(`${skipped.length} match(es) skipped — they have linked requests: ${skipped.slice(0, 3).join(', ')}${skipped.length > 3 ? '...' : ''}`);
      }
      setSelected(new Set());
      fetchMatches();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBulkDeleting(false);
    setBulkConfirmOpen(false);
  };

  const statusColor = { upcoming: 'bg-blue-100 text-blue-700', today: 'bg-green-100 text-green-700', tomorrow: 'bg-amber-100 text-amber-700', ended: 'bg-gray-100 text-gray-600', rescheduled: 'bg-purple-100 text-purple-700', closed: 'bg-gray-100 text-gray-600', cancelled: 'bg-red-100 text-red-700', completed: 'bg-green-100 text-green-700' };
  const isManager = hasPerm('create_matches') || hasPerm('edit_matches') || hasPerm('delete_matches');

  // Reusable team select dropdown
  const TeamSelect = ({ value, onChange, testId, exclude }) => (
    <Select value={value || '_placeholder'} onValueChange={v => v !== '_placeholder' && onChange(v)}>
      <SelectTrigger data-testid={testId} className="bg-gray-50 border-gray-300 h-11 text-base">
        <SelectValue placeholder={t('select_team')} />
      </SelectTrigger>
      <SelectContent className="bg-white border-gray-200 max-h-60">
        {clubs.filter(c => c.name !== exclude).map(c => (
          <SelectItem key={c.id} value={c.name}>
            <div className="flex items-center gap-2">
              {c.badge_url && <img src={c.badge_url} alt="" className="h-5 w-5 object-contain" />}
              <span>{c.name}</span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  // Reusable stadium select
  const StadiumSelect = ({ value, onSelect, testId }) => (
    <Select value={value || '_placeholder'} onValueChange={v => v !== '_placeholder' && onSelect(v)}>
      <SelectTrigger data-testid={testId} className="bg-gray-50 border-gray-300 h-11 text-base">
        <SelectValue placeholder={t('select_stadium')} />
      </SelectTrigger>
      <SelectContent className="bg-white border-gray-200 max-h-60">
        {stadiums.map(s => (
          <SelectItem key={s.name} value={s.name}>
            {s.name} — {s.city}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  // Reusable round select
  const RoundSelect = ({ value, onChange, testId }) => (
    <Select value={value || '_placeholder'} onValueChange={v => v !== '_placeholder' && onChange(v)}>
      <SelectTrigger data-testid={testId} className="bg-gray-50 border-gray-300 h-11 text-base">
        <SelectValue placeholder={t('select_round')} />
      </SelectTrigger>
      <SelectContent className="bg-white border-gray-200 max-h-60">
        {rounds.map(r => (
          <SelectItem key={r} value={r}>{r}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const isSponsorRole = user?.role === 'sponsor' || user?.role === 'spo_viewer';
  if (!seasonOpen && isSponsorRole) {
    return (
      <div data-testid="season-closed-matches" className="flex flex-col items-center justify-center h-64 text-center">
        <Lock size={32} className="text-amber-500 mb-3" />
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Season Closed</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-sm">The admin has closed the current season. Matches are not available right now.</p>
      </div>
    );
  }

  return (
    <div data-testid="matches-page" className="space-y-4 sm:space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">{t('matches')}</h1>
          <p className="text-gray-400 mt-0.5 text-xs sm:text-sm">{filtered.length} / {matches.length} {t('matches_in_schedule')}</p>
        </div>
        {isManager && (
          <div className="flex gap-2">
            {user?.permissions?.includes('import_matches') && (
              <Button variant="outline" onClick={() => navigate('/matches/import')} className="text-xs sm:text-sm border-gray-200 h-8 sm:h-9">
                <Upload size={14} className="mr-1" /> {t('import_schedule')}
              </Button>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-match-btn" className="bg-gray-900 hover:bg-gray-800 text-xs sm:text-sm h-8 sm:h-9"><Plus size={14} className="mr-1" /> {t('add_match')}</Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-gray-200 max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="text-lg font-semibold text-gray-900">{t('new_match')}</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-base text-gray-700">{t('home_team')} *</Label>
                      <TeamSelect value={form.home_team} onChange={v => setForm(p => ({...p, home_team: v}))} testId="match-home-input" exclude={form.away_team} />
                    </div>
                    <div><Label className="text-base text-gray-700">{t('away_team')} *</Label>
                      <TeamSelect value={form.away_team} onChange={v => setForm(p => ({...p, away_team: v}))} testId="match-away-input" exclude={form.home_team} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-base text-gray-700">{t('round')} *</Label>
                    <Input data-testid="match-round-input" type="number" min="1" max="34" placeholder="e.g. 1" value={form.round} onChange={e => setForm(p => ({...p, round: e.target.value}))} className="bg-gray-50 border-gray-300 h-11 text-base" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-base text-gray-700">{t('match_date')} *</Label><Input type="date" value={form.match_date} onChange={e => setForm(p => ({...p, match_date: e.target.value}))} className="bg-gray-50 border-gray-300 h-11 text-base" /></div>
                    <div><Label className="text-base text-gray-700">{t('kickoff_time')}</Label><Input type="time" step="60" value={form.kickoff_time} onChange={e => setForm(p => ({...p, kickoff_time: e.target.value}))} className="bg-gray-50 border-gray-300 h-11 text-base" /></div>
                  </div>
                  <div>
                    <Label className="text-base text-gray-700">{t('stadium')}</Label>
                    <StadiumSelect value={form.stadium} onSelect={v => handleStadiumSelect(v, setForm)} testId="match-stadium-select" />
                  </div>
                  <div><Label className="text-base text-gray-700">{t('notes')}</Label><Textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} className="bg-gray-50 border-gray-300 text-base" /></div>
                  <div>
                    <Label className="text-base text-gray-700">Season *</Label>
                    <Select value={form.season || seasonFilter} onValueChange={v => setForm(p => ({...p, season: v}))}>
                      <SelectTrigger className="bg-gray-50 border-gray-300 h-11 text-base"><SelectValue placeholder="Select season" /></SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        {seasons.filter(s => s.status === 'active').map(s => (
                          <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button data-testid="save-match-btn" onClick={handleCreate} className="w-full bg-gray-900 hover:bg-gray-800 text-sm h-10">{t('add_match')}</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Filters - Always visible: season + search. Others behind toggle on mobile */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <Select value={seasonFilter} onValueChange={v => { setSeasonFilter(v); setSelected(new Set()); }}>
            <SelectTrigger data-testid="season-filter" className="w-32 sm:w-40 bg-white border-gray-300 h-9 sm:h-10 text-xs sm:text-sm font-medium"><SelectValue placeholder="Season" /></SelectTrigger>
            <SelectContent className="bg-white border-gray-200">
              {seasons.filter(s => s.status === 'active').map(s => (
                <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
              ))}
              <SelectItem value="all">All Seasons</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative flex-1 min-w-[140px] max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input data-testid="match-search" placeholder={t('search_matches')} value={search} onChange={e => setSearch(e.target.value)} className="pl-8 bg-white border-gray-300 h-9 sm:h-10 text-xs sm:text-sm" />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setFiltersOpen(!filtersOpen)}
            className={`lg:hidden h-9 text-xs border-gray-300 ${filtersOpen ? 'bg-gray-100 dark:bg-gray-800' : ''}`}
          >
            <SlidersHorizontal size={13} className="mr-1" /> Filters
          </Button>
          {/* Desktop-only inline filters */}
          <div className="hidden lg:contents">
            <Select value={teamFilter} onValueChange={setTeamFilter}>
              <SelectTrigger data-testid="team-filter" className="w-44 bg-white border-gray-300 h-10 text-sm"><SelectValue placeholder={t('all_teams')} /></SelectTrigger>
              <SelectContent className="bg-white border-gray-200 max-h-60">
                <SelectItem value="all">{t('all_teams')}</SelectItem>
                {allTeams.map(team => (
                  <SelectItem key={team} value={team}>
                    <div className="flex items-center gap-2"><TeamLogo name={team} size={16} /><span>{team}</span></div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={roundFilter} onValueChange={setRoundFilter}>
              <SelectTrigger data-testid="round-filter" className="w-40 bg-white border-gray-300 h-10 text-sm"><SelectValue placeholder="All Rounds" /></SelectTrigger>
              <SelectContent className="bg-white border-gray-200 max-h-60">
                <SelectItem value="all">All Rounds</SelectItem>
                {matchRounds.map(r => (
                  <SelectItem key={r} value={r}>{t('round')} {r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input data-testid="date-filter" type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} className="bg-white border-gray-300 h-10 text-sm w-40" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36 bg-white border-gray-300 h-10 text-sm"><SelectValue placeholder={t('all_statuses')} /></SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                <SelectItem value="all">{t('all_statuses')}</SelectItem>
                <SelectItem value="upcoming">{t('upcoming')}</SelectItem>
                <SelectItem value="today">{t('today') || 'Today'}</SelectItem>
                <SelectItem value="tomorrow">{t('tomorrow') || 'Tomorrow'}</SelectItem>
                <SelectItem value="ended">{t('ended') || 'Ended'}</SelectItem>
                <SelectItem value="rescheduled">{t('rescheduled') || 'Rescheduled'}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-500 hover:text-gray-700 h-9 sm:h-10 text-xs">
              <X size={13} className="mr-1" /> Clear
            </Button>
          )}
          {endedCount > 0 && (
            <Button
              data-testid="toggle-ended-btn"
              variant={showEnded ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowEnded(!showEnded)}
              className={showEnded ? 'bg-gray-900 hover:bg-gray-800 text-white h-9 sm:h-10 text-xs sm:text-sm' : 'border-gray-300 text-gray-600 hover:bg-gray-50 h-9 sm:h-10 text-xs sm:text-sm'}
            >
              <Archive size={13} className="mr-1" /> {showEnded ? 'Hide' : `Ended (${endedCount})`}
            </Button>
          )}
        </div>
        {/* Mobile expanded filters */}
        {filtersOpen && (
          <div className="lg:hidden grid grid-cols-2 gap-2 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <Select value={teamFilter} onValueChange={setTeamFilter}>
              <SelectTrigger data-testid="team-filter-mobile" className="bg-white border-gray-300 h-9 text-xs"><SelectValue placeholder={t('all_teams')} /></SelectTrigger>
              <SelectContent className="bg-white border-gray-200 max-h-60">
                <SelectItem value="all">{t('all_teams')}</SelectItem>
                {allTeams.map(team => (
                  <SelectItem key={team} value={team}>
                    <div className="flex items-center gap-2"><TeamLogo name={team} size={14} /><span className="text-xs">{team}</span></div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={roundFilter} onValueChange={setRoundFilter}>
              <SelectTrigger data-testid="round-filter-mobile" className="bg-white border-gray-300 h-9 text-xs"><SelectValue placeholder="All Rounds" /></SelectTrigger>
              <SelectContent className="bg-white border-gray-200 max-h-60">
                <SelectItem value="all">All Rounds</SelectItem>
                {matchRounds.map(r => (
                  <SelectItem key={r} value={r}>{t('round')} {r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input data-testid="date-filter-mobile" type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} className="bg-white border-gray-300 h-9 text-xs" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="bg-white border-gray-300 h-9 text-xs"><SelectValue placeholder={t('all_statuses')} /></SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                <SelectItem value="all">{t('all_statuses')}</SelectItem>
                <SelectItem value="upcoming">{t('upcoming')}</SelectItem>
                <SelectItem value="today">{t('today') || 'Today'}</SelectItem>
                <SelectItem value="tomorrow">{t('tomorrow') || 'Tomorrow'}</SelectItem>
                <SelectItem value="ended">{t('ended') || 'Ended'}</SelectItem>
                <SelectItem value="rescheduled">{t('rescheduled') || 'Rescheduled'}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Bulk delete bar */}
      {bulkDeleteEnabled && hasPerm('delete_matches') && selected.size > 0 && (
        <div className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg">
          <span className="text-sm font-medium text-red-700 dark:text-red-400">{selected.size} selected</span>
          <Button data-testid="bulk-delete-btn" size="sm" variant="destructive" onClick={() => setBulkConfirmOpen(true)} className="h-8 text-xs">
            <Trash2 size={13} className="mr-1" /> Delete Selected
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="h-8 text-xs text-gray-500">Clear</Button>
          <ConfirmDialog open={bulkConfirmOpen} onCancel={() => setBulkConfirmOpen(false)} onConfirm={handleBulkDelete}
            title="Delete Matches" description={`Are you sure you want to delete ${selected.size} matches? This cannot be undone.`} confirmText={bulkDeleting ? 'Deleting...' : 'Delete'} destructive />
        </div>
      )}

      {/* Table */}
      <Card className="bg-white border-0 rounded-card shadow-card">
        <CardContent className="p-0 overflow-x-auto">
          <Table className="min-w-[600px]">
            <TableHeader>
              <TableRow className="border-gray-100 hover:bg-transparent">
                {bulkDeleteEnabled && hasPerm('delete_matches') && (
                  <TableHead className="w-10 text-center">
                    <input type="checkbox" className="h-4 w-4 rounded border-gray-300"
                      checked={filtered.length > 0 && selected.size === filtered.length}
                      onChange={toggleSelectAll} />
                  </TableHead>
                )}
                <TableHead className="text-[10px] sm:text-xs text-gray-400 font-medium">{t('home_team')}</TableHead>
                <TableHead className="text-[10px] sm:text-xs text-gray-400 font-medium w-8 text-center px-0"></TableHead>
                <TableHead className="text-[10px] sm:text-xs text-gray-400 font-medium">{t('away_team')}</TableHead>
                <TableHead className="text-[10px] sm:text-xs text-gray-400 font-medium hidden lg:table-cell">{t('round')}</TableHead>
                <TableHead className="text-[10px] sm:text-xs text-gray-400 font-medium">{t('date')}</TableHead>
                <TableHead className="text-[10px] sm:text-xs text-gray-400 font-medium hidden sm:table-cell">{t('kickoff_time')}</TableHead>
                <TableHead className="text-[10px] sm:text-xs text-gray-400 font-medium hidden lg:table-cell">{t('venue')}</TableHead>
                <TableHead className="text-[10px] sm:text-xs text-gray-400 font-medium">{t('status')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={9} className="text-center py-8 text-gray-400 text-sm">{t('loading')}</TableCell></TableRow> :
              groupedByRound.length === 0 ? <TableRow><TableCell colSpan={9} className="text-center py-8 text-gray-400 text-sm">{t('no_matches')}</TableCell></TableRow> :
              groupedByRound.map((item, idx) => {
                if (item.type === 'header') {
                  return (
                    <TableRow key={`round-${item.round}-${idx}`} className="bg-gray-50/70 dark:bg-gray-800/70 hover:bg-gray-50/70 dark:hover:bg-gray-800/70 border-gray-100 dark:border-gray-700">
                      <TableCell colSpan={9} className="py-1.5 px-4">
                        <span className="text-[10px] sm:text-[11px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">{t('round')} {item.round}</span>
                      </TableCell>
                    </TableRow>
                  );
                }
                const m = item.data;
                return (
                <TableRow key={m.id} className="border-gray-100 hover:bg-gray-50/70 h-11 sm:h-12 cursor-pointer group" data-testid={`match-row-${m.id}`} onClick={() => setDetailMatch(m)}>
                  {bulkDeleteEnabled && hasPerm('delete_matches') && (
                    <TableCell className="py-1.5 sm:py-2 text-center" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" className="h-4 w-4 rounded border-gray-300"
                        checked={selected.has(m.id)}
                        onChange={() => toggleSelect(m.id)} />
                    </TableCell>
                  )}
                  <TableCell className="py-1.5 sm:py-2 whitespace-nowrap">
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <TeamLogo name={m.home_team} badgeUrl={m.home_team_badge} size={18} className="sm:w-[22px] sm:h-[22px]" />
                      <span className="font-semibold text-gray-800 text-xs sm:text-sm">{m.home_team}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-center px-0 py-1.5 sm:py-2">
                    {m.status === 'ended' && (m.home_score != null && m.away_score != null)
                      ? <span className="text-[10px] sm:text-xs font-bold text-gray-800">{m.home_score} - {m.away_score}</span>
                      : <span className="text-gray-400 text-[9px] sm:text-[10px] font-bold">VS</span>
                    }
                  </TableCell>
                  <TableCell className="py-1.5 sm:py-2 whitespace-nowrap">
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <TeamLogo name={m.away_team} badgeUrl={m.away_team_badge} size={18} className="sm:w-[22px] sm:h-[22px]" />
                      <span className="font-semibold text-gray-800 text-xs sm:text-sm">{m.away_team}</span>
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-gray-500 text-xs py-2 whitespace-nowrap">{m.round}</TableCell>
                  <TableCell className="text-gray-700 text-xs sm:text-sm py-1.5 sm:py-2 whitespace-nowrap">{m.match_date}</TableCell>
                  <TableCell className="hidden sm:table-cell text-gray-700 text-xs sm:text-sm py-2 whitespace-nowrap">{m.kickoff_time || '---'}</TableCell>
                  <TableCell className="hidden lg:table-cell py-2 whitespace-nowrap max-w-[220px]">
                    <span className="text-gray-400 text-xs truncate block">{m.stadium}{m.city ? `, ${m.city}` : ''}</span>
                  </TableCell>
                  <TableCell className="py-2">
                    <div className="flex items-center gap-1.5">
                      <Badge className={`text-[11px] pointer-events-none select-none ${statusColor[m.status] || 'bg-gray-100 text-gray-600'}`}>{m.status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</Badge>
                      {m.label && MATCH_LABELS[m.label] && (
                        <Badge className={`text-[10px] pointer-events-none select-none ${MATCH_LABELS[m.label].color}`}>
                          {MATCH_LABELS[m.label].name}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Match Detail Dialog */}
      <MatchDetailDialog
        match={detailMatch}
        onClose={() => setDetailMatch(null)}
        onMatchUpdate={(updated) => {
          setDetailMatch(updated);
          setMatches(prev => prev.map(m => m.id === updated.id ? { ...m, ...updated } : m));
        }}
        onEdit={(m) => handleEdit(m)}
      />

      {/* Edit Match Dialog */}
      <Dialog open={editOpen} onOpenChange={(o) => { if (!o) { setEditOpen(false); setEditForm(null); } else setEditOpen(true); }}>
        <DialogContent className="bg-white border-gray-200 max-w-lg">
          <DialogHeader><DialogTitle className="text-base font-semibold text-gray-900">{t('edit_match') || 'Edit Match'}</DialogTitle></DialogHeader>
          {editForm && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">{t('home_team')} *</Label>
                  <TeamSelect value={editForm.home_team} onChange={v => setEditForm(p => ({...p, home_team: v}))} testId="edit-home-input" exclude={editForm.away_team} />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">{t('away_team')} *</Label>
                  <TeamSelect value={editForm.away_team} onChange={v => setEditForm(p => ({...p, away_team: v}))} testId="edit-away-input" exclude={editForm.home_team} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">{t('round')}</Label>
                  <Input data-testid="edit-round-input" type="number" min="1" max="34" placeholder="e.g. 1" value={editForm.round || ''} onChange={e => setEditForm(p => ({...p, round: e.target.value}))} className="bg-gray-50 border-gray-200 h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">{t('match_date')} *</Label>
                  <Input data-testid="edit-date-input" type="date" value={editForm.match_date || ''} onChange={e => setEditForm(p => ({...p, match_date: e.target.value}))} className="bg-gray-50 border-gray-200 h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">{t('kickoff_time')}</Label>
                  <Input type="time" step="60" value={editForm.kickoff_time || ''} onChange={e => setEditForm(p => ({...p, kickoff_time: e.target.value}))} className="bg-gray-50 border-gray-200 h-9 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">{t('stadium')}</Label>
                  <StadiumSelect value={editForm.stadium} onSelect={v => handleStadiumSelect(v, setEditForm)} testId="edit-stadium-select" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">{t('status')}</Label>
                  <Select value={editForm.status || 'upcoming'} onValueChange={v => setEditForm(p => ({...p, status: v}))}>
                    <SelectTrigger data-testid="edit-status-select" className="bg-gray-50 border-gray-200 h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      <SelectItem value="upcoming">{t('upcoming')}</SelectItem>
                      <SelectItem value="today">{t('today') || 'Today'}</SelectItem>
                      <SelectItem value="tomorrow">{t('tomorrow') || 'Tomorrow'}</SelectItem>
                      <SelectItem value="ended">{t('ended') || 'Ended'}</SelectItem>
                      <SelectItem value="rescheduled">{t('rescheduled') || 'Rescheduled'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">Season</Label>
                  <Select value={editForm.season || ''} onValueChange={v => setEditForm(p => ({...p, season: v}))}>
                    <SelectTrigger className="bg-gray-50 border-gray-200 h-9 text-sm"><SelectValue placeholder="Select season" /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {seasons.filter(s => s.status === 'active').map(s => (
                        <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Label</Label>
                  <Select value={editForm.label || '_none'} onValueChange={v => setEditForm(p => ({...p, label: v === '_none' ? '' : v}))}>
                    <SelectTrigger data-testid="edit-label-select" className="bg-gray-50 border-gray-200 h-9 text-sm"><SelectValue placeholder="No label" /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      <SelectItem value="_none">No Label</SelectItem>
                      {Object.entries(MATCH_LABELS).map(([key, cfg]) => (
                        <SelectItem key={key} value={key}>
                          <span className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${cfg.dot}`} /> {cfg.name}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-500">{t('notes')}</Label>
                <Input value={editForm.notes || ''} onChange={e => setEditForm(p => ({...p, notes: e.target.value}))} placeholder="Optional notes" className="bg-gray-50 border-gray-200 h-9 text-sm" />
              </div>
              <DialogFooter className="flex gap-2 pt-1">
                <Button
                  data-testid="delete-match-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmOpen(true)}
                  disabled={saving}
                  className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 h-8 text-xs"
                >
                  <Trash2 size={13} className="mr-1" /> {t('delete') || 'Delete'}
                </Button>
                <ConfirmDialog open={confirmOpen} onCancel={() => setConfirmOpen(false)} onConfirm={handleDelete}
                  title="Delete Match" description="Are you sure you want to delete this match?" confirmText="Delete" destructive />
                <div className="flex-1" />
                <Button variant="outline" size="sm" onClick={() => { setEditOpen(false); setEditForm(null); }} disabled={saving} className="border-gray-200 h-8 text-xs">
                  {t('cancel') || 'Cancel'}
                </Button>
                <Button
                  data-testid="save-edit-match-btn"
                  size="sm"
                  onClick={handleSaveEdit}
                  disabled={saving}
                  className="bg-gray-900 hover:bg-gray-800 h-8 text-xs"
                >
                  {saving ? (t('saving') || 'Saving...') : (t('save_changes') || 'Save Changes')}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
